import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { type WorkQueueCase, type OutgoingAction, advanceWorkflow } from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { useToast } from '../../providers/ToastProvider';
import { reportError } from '../../services/errorReporting';
import { AgingBadge } from './AgingBadge';
import { AGING_EXEMPT_STATUS_IDS } from './constants';
import { computeCaseTags } from './caseTags';
import { CaseTagList } from './CaseTagPills';
import { invalidateCaseDetail } from './util';
import { StatusChip } from './StatusChip';
import { statusPreview } from './statusPreview';
import { apiErrorMessage } from '../../utils/apiError';
import { useEscapeKey } from '../../hooks/useEscapeKey';

function getAgingLabel(days: number): string {
    if (days === 0) return 'Today';
    if (days === 1) return '1 day';
    return `${days}d`;
}

const MS_PER_DAY = 86_400_000;
function ageDaysFromIso(iso: string): number {
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return 0;
    return Math.max(0, Math.floor((Date.now() - ts) / MS_PER_DAY));
}

interface ActionTarget {
    workCase: WorkQueueCase;
    outgoingAction: OutgoingAction;
}

interface ActionModalProps {
    actionTarget: ActionTarget | null;
    onClose: () => void;
}

export function ActionModal({ actionTarget, onClose }: ActionModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const catalog = useStatusCatalog();

    // WorkQueue mounts this unconditionally, so the guard - not the early return
    // below - is what keeps the listener off while no action is targeted.
    useEscapeKey(onClose, actionTarget !== null && !isSubmitting);

    if (!actionTarget) return null;

    const { workCase: c, outgoingAction } = actionTarget;

    const stepAgeDays = ageDaysFromIso(c.stepEnteredAt ?? c.updatedAt);
    const agingExempt = AGING_EXEMPT_STATUS_IDS.has(c.statusId);

    const handleConfirm = async () => {
        setIsSubmitting(true);
        try {
            const result = await advanceWorkflow(c.id, outgoingAction.action);
            // Case-detail invalidation (not just the queue list): the detail
            // panel's timeline/journey/card queries would otherwise serve
            // stale cache after the status change (see util.ts convention).
            await invalidateCaseDetail(queryClient, c.id);
            if (result.conversationMirrorFailed) {
                // No warning tone exists (success/error only) - error tone with
                // softer copy reads better than a false "all good" success toast.
                showToast(
                    `Case advanced: ${outgoingAction.label}, but the revision note didn't post to the conversation.`,
                    'error'
                );
            } else {
                showToast(`Case advanced: ${outgoingAction.label}`, 'success');
            }
            onClose();
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to advance case. Please try again.');
            showToast(message, 'error');
            reportError({
                source: 'ActionModal.handleConfirm',
                message,
                stack: err instanceof Error ? err.stack : undefined,
                url: globalThis.location?.href ?? '',
                userAgent: navigator.userAgent,
                errorType: 'workflow_action_failure',
                metadata: { caseId: c.id, action: outgoingAction.action }
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-35 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl w-[460px] shadow-xl overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200">
                    <div className="font-bold text-[15px] text-gray-800 mb-0.5">
                        {outgoingAction.label}
                    </div>
                    <div className="text-xs text-gray-500">
                        {c.name} · {c.practice}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        <CaseTagList tags={computeCaseTags(c)} size="md" />
                    </div>
                </div>

                {/* Body */}
                <div className="px-5 py-4">
                    <div className="bg-gray-50 rounded-md px-3 py-2.5 mb-3.5 text-xs text-gray-600 space-y-2.5">
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-2.5 items-center">
                            <StatusChip preview={statusPreview(c.statusId, catalog)} />
                            <ArrowLongRightIcon className="w-5 h-5 text-gray-400" />
                            <StatusChip preview={statusPreview(outgoingAction.to, catalog)} />
                        </div>
                        <div className="flex items-center gap-1.5">
                            <strong>In status:</strong>
                            <AgingBadge days={stepAgeDays} neutral={agingExempt} />
                            <span
                                className={
                                    !agingExempt && stepAgeDays > 7
                                        ? 'text-red-600 font-semibold'
                                        : ''
                                }
                            >
                                {getAgingLabel(stepAgeDays)}
                            </span>
                        </div>
                    </div>

                    <button
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className="w-full py-2.5 rounded-md bg-gray-800 text-white font-bold text-[13px] hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                    >
                        {isSubmitting ? 'Advancing…' : outgoingAction.label}
                    </button>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="w-full py-1.5 rounded-md border border-gray-200 text-gray-500 text-xs font-medium hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
